import React, { useCallback } from 'react'
import { BaseCardProps, cardViewabilityDebug } from '../Card'
import {
    createCollectionAvailableEvent,
    isPublicationCardItemWithVideo,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'
import {
    Bullet,
    Teaser,
    TopicBylineWrapper,
    TopicLink,
    AuthorLink,
    CardContainer,
    StyledCardMedia,
    Author,
    Classification,
} from '../components'
import {
    StyledHeadline,
    StyledCardBodyWrapper,
    CardLinkWrapper,
    StyledReadMore,
} from './ListCard.styled'
import { getByline, getClassification, getTopicLink } from '../utils'
import { CommentsCount } from '../components/card-body/byline/CommentsCount'
import { EditorialType, FundingType } from '../../../templates'
import { TNLiveComponent } from '../../../content'
import { useProduct } from '../../../__product'
import { useImpressionAvailable } from '../../../__helpers/impression-available-helper'

export interface ListCardProps extends BaseCardProps<'list-card'> {
    canPlayVideoInline?: boolean
}

export const ListCard = ({
    item,
    onEvent,
    canPlayVideoInline,
    disableImageLazyLoad,
    imageFetchPriority,
}: Omit<ListCardProps, 'kind'>) => {
    const classificationEnabled = useFeature('story-classification')
    const product = useProduct()

    const { loaded: isLoaded } = item
    const result = isLoaded ? item.result : undefined
    const articleLink = toLinkWithHint(item)
    const byline = getByline(item)
    const topicLink = getTopicLink(item)
    const classification = getClassification(item, classificationEnabled)
    const showNormalClassification = classification !== EditorialType.Live
    const isVideo = result && isPublicationCardItemWithVideo(result)
    const showClassification =
        classification !== undefined && classification !== EditorialType.Live

    // Impression Available Function
    const availableFunction = useCallback(() => {
        if (!isLoaded || result === undefined) {
            console.warn(
                'Available Function should never be called when loading is true',
            )
            return
        }

        // Add a debug for viewability!
        cardViewabilityDebug(
            'ListCard',
            `in frame and has been viewed with a size of ${1}`,
        )

        // Send an onEvent when the items are available in view
        onEvent(
            createCollectionAvailableEvent(
                [result],
                'ListCard',
                product,
                onEvent,
            ),
        )
    }, [isLoaded, onEvent, product, result])

    // Impression Available Ref
    const impressionAvailableRef = useImpressionAvailable({
        loading: !isLoaded,
        available: availableFunction,
        debug: cardViewabilityDebug.extend('list-card'),
    })

    return (
        <CardContainer ref={impressionAvailableRef}>
            {isVideo && canPlayVideoInline ? (
                <ListCardMedia
                    item={item}
                    onEvent={onEvent}
                    canPlayVideoInline={canPlayVideoInline}
                    disableImageLazyLoad={disableImageLazyLoad}
                    imageFetchPriority={imageFetchPriority}
                />
            ) : (
                <CardLinkWrapper to={articleLink}>
                    <ListCardMedia
                        item={item}
                        onEvent={onEvent}
                        disableImageLazyLoad={disableImageLazyLoad}
                        imageFetchPriority={'high'}
                        classification={classification}
                    />
                </CardLinkWrapper>
            )}
            {result && (
                <StyledCardBodyWrapper>
                    <TopicBylineWrapper>
                        {showClassification && (
                            <>
                                <Classification type={classification}>
                                    {classification}
                                </Classification>
                                <Bullet />
                            </>
                        )}
                        <TopicLink to={topicLink}>
                            {result.primaryTopic.title}
                        </TopicLink>
                        <Bullet />
                        {!byline.link ? (
                            <Author>{byline.text}</Author>
                        ) : (
                            <AuthorLink to={byline.link}>
                                {byline.text}
                            </AuthorLink>
                        )}
                        {result.allowCommenting && (
                            <>
                                <Bullet />
                                <CommentsCount articleUrl={result._self} />
                            </>
                        )}
                    </TopicBylineWrapper>
                    <CardLinkWrapper to={articleLink}>
                        <StyledHeadline>{result.shortHeadline}</StyledHeadline>
                        <Teaser>{result.teaser}</Teaser>
                        <StyledReadMore readTime={result.readTimeMinutes} />
                    </CardLinkWrapper>
                </StyledCardBodyWrapper>
            )}
        </CardContainer>
    )
}

type ListCardMediaProps = Pick<
    BaseCardProps<any>,
    'onEvent' | 'item' | 'disableImageLazyLoad' | 'imageFetchPriority'
> & {
    canPlayVideoInline?: boolean
    classification?: EditorialType | FundingType | undefined
}

export const ListCardMedia = ({
    item,
    onEvent,
    canPlayVideoInline,
    disableImageLazyLoad,
    imageFetchPriority,
    classification,
}: ListCardMediaProps) => {
    const liveClassification = classification === EditorialType.Live

    return (
        <>
            {liveClassification && (
                <TNLiveComponent overlayOnImage={true} showBackground={true} />
            )}
            <StyledCardMedia
                fixedRatio={{
                    default: '4:3',
                    sm: canPlayVideoInline ? '16:9' : undefined,
                }}
                CSSCrop={{
                    default: {
                        width: 770,
                        height: 577,
                    },
                }}
                imageWidths={{
                    desktop: '100%',
                    tablet: '100%',
                    mobile: '100%',
                    fallbackWidth: 770,
                }}
                item={item}
                onEvent={onEvent}
                disableImageLazyLoad={disableImageLazyLoad}
                imageFetchPriority={imageFetchPriority}
                mediaMode={'fullHeight'}
                isLarge={canPlayVideoInline}
                willPlayVideoInline={canPlayVideoInline}
            />
        </>
    )
}
